/**
 * State/label/tone maps for the payroll-connection panels.
 *
 * Tone tokens are the `UserStatePill` class suffixes, so every value here has
 * to exist in `common/userStatePill/styles/user-state-pill.module.scss`:
 * success | pending | info | missing | neutral | error | danger. There is no
 * `warn` and no `unknown` — an unmapped tone silently falls back to neutral,
 * which is why the maps below are exhaustive and default explicitly.
 */

import { classificationFor, toneFor, NEUTRAL_TONE } from '../statusClassification/statusClassification';

/**
 * The URL-safe key the summary endpoint uses for sync rows that never received
 * an `account_id` — the member opened Link and nothing ever came back. Mirrors
 * `Constants.PAYROLL_CONNECTION.UNASSIGNED_KEY` in clockout-service
 * `routes/constants.js`, which both payroll-connection routes read it from.
 */
export const UNASSIGNED_CONNECTION_KEY = 'unassigned';

/**
 * How many events the connection card shows inline before deferring to the
 * full-history modal. Four events per link attempt means a relinked job fills
 * the rail fast; five rows is one attempt plus context, and the rest is one
 * click away.
 */
export const PREVIEW_EVENT_COUNT = 5;

/** Platform display name the API sends when a platform is null/unknown. */
export const PLATFORM_DISPLAY_NAME_UNSET = 'Not set';

/** Connection states returned by the summary endpoint. */
export const CONNECTION_STATES = Object.freeze({
    CONNECTED: 'connected',
    NOT_CONNECTED: 'not_connected',
    IN_PROGRESS: 'in_progress',
    FAILED: 'failed',
    UNRESOLVED: 'unresolved',
});

/** Event kinds returned by the events endpoint. */
export const SYNC_EVENT_KINDS = Object.freeze({
    CREATED: 'created',
    LOGIN: 'login',
    COMPLETE: 'complete',
    FAILED: 'failed',
    DD_SWITCH: 'dd_switch',
});

/** `sync` row statuses carried on each event. */
export const SYNC_STATUSES = Object.freeze({
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETE: 'COMPLETE',
    RETRY: 'RETRY',
    PARTIAL: 'PARTIAL',
    FAILED: 'FAILED',
    DIRECT_DEPOSIT_SWITCH: 'DIRECT_DEPOSIT_SWITCH',
});

const EVENT_KIND_LABELS = Object.freeze({
    [SYNC_EVENT_KINDS.CREATED]: 'Link started',
    [SYNC_EVENT_KINDS.LOGIN]: 'Signed in',
    [SYNC_EVENT_KINDS.COMPLETE]: 'Sync settled',
    [SYNC_EVENT_KINDS.FAILED]: 'Sync failed',
    [SYNC_EVENT_KINDS.DD_SWITCH]: 'Direct deposit switch',
});

/**
 * Outcome copy + tone per `sync` status.
 *
 * PARTIAL and RETRY are `pending`, not `success`: the row settled without
 * every required dimension, and there is no `warn` tone to reach for. Reading
 * them as success is exactly the failure mode the story calls out.
 */
const EVENT_STATUS_DISPLAY = Object.freeze({
    [SYNC_STATUSES.COMPLETE]: { label: 'Complete' },
    [SYNC_STATUSES.PARTIAL]: { label: 'Partial' },
    [SYNC_STATUSES.RETRY]: { label: 'Retrying' },
    [SYNC_STATUSES.IN_PROGRESS]: { label: 'No outcome reported' },
    [SYNC_STATUSES.FAILED]: { label: 'Failed' },
    [SYNC_STATUSES.DIRECT_DEPOSIT_SWITCH]: { label: 'DD switch' },
});

const UNKNOWN_STATUS_DISPLAY = Object.freeze({ label: 'No outcome reported' });

/**
 * Row label for an event kind — what happened, not how it turned out.
 * @param {string | null | undefined} kind
 * @returns {string}
 */
export function getEventKindLabel(kind) {
    return EVENT_KIND_LABELS[kind] || 'Sync event';
}

/**
 * How an event's `status` reads: label from this module, tone from the served
 * sync-state catalog like every other status pill (statusClassification.toneFor).
 * A status the catalog does not classify (DIRECT_DEPOSIT_SWITCH, which the
 * service deliberately leaves out) renders neutral without raising the
 * unclassified-state error.
 * @param {string | null | undefined} status
 * @returns {{ label: string, tone: string }}
 */
export function getEventStatusDisplay(status) {
    const entry = EVENT_STATUS_DISPLAY[status] || UNKNOWN_STATUS_DISPLAY;
    const tone = classificationFor(status) ? toneFor(status) : NEUTRAL_TONE;
    return { label: entry.label, tone };
}

/**
 * React key for one event row.
 *
 * Events carry no id of their own: they are fanned out from `sync` rows by
 * `syncEventsFor`, and one row emits up to four of them. The readable half of
 * the key is the identity the row renders — the token it belongs to, what
 * happened, and when — and `index` is appended unconditionally rather than as
 * a fallback, because that triple is NOT unique: a single sync stamps
 * created_at and login_timestamp to the same millisecond often enough that the
 * API has to break the tie by kind rank to page deterministically.
 *
 * Safe as a key because the list only ever grows at the end: a fetched page is
 * appended, so an event's position never shifts once it is on screen.
 *
 * @param {Object} event
 * @param {number} index Position in the list.
 * @returns {string}
 */
export function getEventRowKey(event, index) {
    return `${event?.link_token_id || 'event'}-${event?.kind || 'kind'}-${event?.at || 'at'}-${index}`;
}

/**
 * Platform label for a connection, already normalized by the API. Falls back
 * to the same "Not set" the backend uses so a missing platform object and a
 * null platform name read identically.
 *
 * @param {{ platform?: { display_name?: string | null } | null } | null | undefined} connection
 * @returns {string}
 */
export function getConnectionPlatformLabel(connection) {
    const label = connection?.platform?.display_name;
    return typeof label === 'string' && label.trim() ? label : PLATFORM_DISPLAY_NAME_UNSET;
}
